// Only modifying the logEvent function to prevent cascading errors
import { supabase } from "@/integrations/supabase/client";
import { SUPABASE_URL } from "@/integrations/supabase/client";
import { useCallback } from "react";

// Logger utility functions
type LogLevel = 'error' | 'warning' | 'info';
type LogDetails = Record<string, any> | string | null;

export interface LogEvent {
  message: string;
  location: string;
  details?: LogDetails;
}

// Helper to safely stringify details object
const safeStringify = (details: LogDetails): string | null => {
  if (!details) return null;
  
  try {
    return typeof details === 'string' ? details : JSON.stringify(details);
  } catch (e) {
    console.error('Failed to stringify log details:', e);
    return String(details);
  }
};

// Cache for settings to reduce DB calls
const settingsCache = {
  enableClientLogging: null as boolean | null,
  enableServerLogging: null as boolean | null,
  lastChecked: 0,
  expiryTime: 60000, // 1 minute
  loggingDisabledMessageTime: 0, // Track when we last logged the disabled message
  loggingDisabledMessageInterval: 300000, // 5 minutes between "logging disabled" messages
};

// Check if logging is enabled
const isLoggingEnabled = async (isServerSide = false): Promise<boolean> => {
  const now = Date.now();
  
  // Refresh cache if expired or not initialized
  if (now - settingsCache.lastChecked > settingsCache.expiryTime || 
      settingsCache.enableClientLogging === null || 
      settingsCache.enableServerLogging === null) {
    try {
      // Get client logging setting
      const { data: clientSetting, error: clientError } = await supabase
        .rpc('fetch_setting', { setting_key: 'enable_client_logging' });
      
      if (!clientError && clientSetting !== null) {
        settingsCache.enableClientLogging = clientSetting === 'true';
      } else {
        // Default to false if there's an error or null value
        settingsCache.enableClientLogging = false;
      }
      
      // Get server logging setting
      const { data: serverSetting, error: serverError } = await supabase
        .rpc('fetch_setting', { setting_key: 'enable_server_logging' });
      
      if (!serverError && serverSetting !== null) {
        settingsCache.enableServerLogging = serverSetting === 'true';
      } else {
        // Default to false if there's an error or null value
        settingsCache.enableServerLogging = false;
      }
      
      settingsCache.lastChecked = now;
    } catch (err) {
      // If there's an error checking settings, default to disabled for safety
      console.warn("Error checking logging settings:", err);
      return false;
    }
  }
  
  // Return the appropriate setting based on whether it's server-side or client-side
  const enabled = isServerSide ? 
    !!settingsCache.enableServerLogging : 
    !!settingsCache.enableClientLogging;
  
  // Only log the disabled message occasionally to avoid console spam
  if (!enabled && now - settingsCache.loggingDisabledMessageTime > settingsCache.loggingDisabledMessageInterval) {
    console.info(`Logging ${isServerSide ? 'server' : 'client'} side is disabled`);
    settingsCache.loggingDisabledMessageTime = now;
  }
  
  return enabled;
};

// Main logging function that handles insertion to Supabase
export const logEvent = async (
  level: LogLevel,
  message: string, 
  location: string, 
  details?: LogDetails,
  isServerSide = false
) => {
  try {
    // Check if this type of logging is enabled
    const enabled = await isLoggingEnabled(isServerSide);
    if (!enabled) {
      // Skip logging if disabled
      return;
    }
    
    // Try to get current user info if available
    let user = null;
    try {
      const { data } = await supabase.auth.getUser();
      user = data.user;
    } catch (err) {
      // Silently fail if we can't get user
      console.warn("Failed to get user for logging:", err);
    }
    
    // Insert log into system_logs table
    try {
      const { error } = await supabase
        .from('system_logs')
        .insert({
          level,
          message,
          location,
          details: safeStringify(details),
          user_id: user?.id,
          user_email: user?.email
        });

      if (error) {
        // Don't use recursion here to avoid potential infinite loops
        console.error(`Failed to save log to Supabase:`, error);
      }
    } catch (dbError) {
      // Make sure we don't throw from this catch
      console.warn("Database insert error during logging:", dbError);
    }
  } catch (err) {
    // Just output to console if all else fails
    console.error('Failed to log event:', err);
  }
};

// Convenience wrappers for different log levels
export const logError = (message: string, location: string, details?: LogDetails) => 
  logEvent('error', message, location, details);

export const logWarning = (message: string, location: string, details?: LogDetails) => 
  logEvent('warning', message, location, details);

export const logInfo = (message: string, location: string, details?: LogDetails) => 
  logEvent('info', message, location, details);

// New functions for server-side logging
export const logServerError = (message: string, location: string, details?: LogDetails) => 
  logEvent('error', message, location, details, true);

export const logServerWarning = (message: string, location: string, details?: LogDetails) => 
  logEvent('warning', message, location, details, true);

export const logServerInfo = (message: string, location: string, details?: LogDetails) => 
  logEvent('info', message, location, details, true);

// React hook for component-based logging
export const useLogger = () => {
  const logError = useCallback((message: string, location: string, details?: LogDetails) => {
    return logEvent('error', message, location, details);
  }, []);

  const logWarning = useCallback((message: string, location: string, details?: LogDetails) => {
    return logEvent('warning', message, location, details);
  }, []);

  const logInfo = useCallback((message: string, location: string, details?: LogDetails) => {
    return logEvent('info', message, location, details);
  }, []);

  return {
    logError,
    logWarning,
    logInfo
  };
};

// Setup global error handling
export const setupGlobalErrorLogging = () => {
  // Save original console methods
  const originalConsoleError = console.error;
  const originalConsoleWarn = console.warn;

  // Override console.error
  console.error = (...args) => {
    // Call original to maintain normal console behavior
    originalConsoleError.apply(console, args);
    
    try {
      // Check if client logging is enabled before proceeding
      isLoggingEnabled().then(enabled => {
        if (!enabled) {
          return; // Exit early if logging is disabled
        }
        
        // Log to our system
        const message = args.map(arg => 
          typeof arg === 'string' ? arg : JSON.stringify(arg)
        ).join(' ');
        
        logError(message, 'console.error');
      });
    } catch (err) {
      // If logging fails, just use the original console method
      originalConsoleError("Error during logger operation:", err);
    }
  };

  // Override console.warn
  console.warn = (...args) => {
    // Call original to maintain normal console behavior
    originalConsoleWarn.apply(console, args);
    
    try {
      // Check if client logging is enabled before proceeding
      isLoggingEnabled().then(enabled => {
        if (!enabled) {
          return; // Exit early if logging is disabled
        }
        
        // Log to our system
        const message = args.map(arg => 
          typeof arg === 'string' ? arg : JSON.stringify(arg)
        ).join(' ');
        
        logWarning(message, 'console.warn');
      });
    } catch (err) {
      // If logging fails, just use the original console method
      originalConsoleWarn("Error during logger operation:", err);
    }
  };

  // Add global unhandled error listener
  window.addEventListener('error', (event) => {
    try {
      isLoggingEnabled().then(enabled => {
        if (!enabled) {
          return; // Exit early if logging is disabled
        }
        
        logError(
          event.message || 'Unhandled error',
          event.filename || 'Unknown location',
          {
            line: event.lineno,
            column: event.colno,
            stack: event.error?.stack
          }
        );
      });
    } catch (err) {
      console.error("Error logging unhandled error:", err);
    }
  });

  // Add global unhandled promise rejection listener
  window.addEventListener('unhandledrejection', (event) => {
    try {
      isLoggingEnabled().then(enabled => {
        if (!enabled) {
          return; // Exit early if logging is disabled
        }
        
        const reason = event.reason;
        logError(
          reason?.message || 'Unhandled promise rejection',
          'Promise',
          {
            stack: reason?.stack,
            reason: reason
          }
        );
      });
    } catch (err) {
      console.error("Error logging unhandled rejection:", err);
    }
  });
};

// Function to get logs with filtering - for admin use
export const fetchLogs = async (options: {
  search?: string;
  level?: string;
  limit?: number;
  offset?: number;
}) => {
  const { search = '', level = '', limit = 100, offset = 0 } = options;
  
  // Build query params
  const params = new URLSearchParams();
  if (search) params.append('search', search);
  if (level) params.append('level', level);
  params.append('limit', limit.toString());
  params.append('offset', offset.toString());
  
  try {
    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }
    
    // Call Edge Function
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/admin-get-logs?${params.toString()}`, 
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        }
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Error fetching logs');
    }
    
    return await response.json();
  } catch (error) {
    console.error("Error fetching logs:", error);
    throw error;
  }
};

// Function to clear logs with level filtering
export const clearLogs = async (level: string) => {
  try {
    // Get auth token
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session?.access_token) {
      throw new Error('Not authenticated');
    }
    
    // Call Edge Function
    const response = await fetch(
      `${SUPABASE_URL}/functions/v1/admin-clear-logs`,
      {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${session.access_token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ level })
      }
    );
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || 'Error clearing logs');
    }
    
    return await response.json();
  } catch (error) {
    console.error("Error clearing logs:", error);
    throw error;
  }
};
